import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import moment from 'moment';
import {
  setUserDataLocalSyncListener,
  USER_DATA_CATEGORY_STORAGE_KEYS,
  UserDataSyncCategory,
} from '@common/user-data-sync/user-data-local-sync';
import { UserDataApiService } from './user-data-api.service';
import { UserStoreQueries } from '@/app/platform/platform-core/user/store/user-store.queries';
import { PlatformInstanceListener } from '@/app/platform/platform-core/user/listeners/platform-instance-listener';
import { PlatformUserDataDto } from '@data/_generated-api/platform-api/models/platform-user-data-dto';
import { PlatformModeService } from '@/app/platform/platform-core/platform-mode/platform-mode.service';

export const USER_DATA_SYNC_META_STORAGE_KEY = 'userDataSyncMeta';

/**
 * Wachttijd per categorie vóór een push (de teller herstart bij elke nieuwe wijziging). Instellingen
 * wijzigen zelden en klein: 2 s. De app-status wijzigt bij elke tabwissel en is groot (alle tabs met
 * hun volledige preset, ~125 KB voor 10 tabs): 9 s laat ruim tijd om door de tabs te bladeren zonder
 * na elke tab de hele blob te versturen (afgestemd met Steven, 2026-10-06). Tab verbergen, het
 * `online`-event en hub-reconnect pushen nog altijd meteen.
 */
const PUSH_DEBOUNCE_MS: Readonly<Record<UserDataSyncCategory, number>> = {
  settings: 2000,
  appState: 9000,
};
/** Begrensde retry-cyclus na een mislukte push: 1e retry na 5 s, 2e en laatste na 60 s. */
const RETRY_DELAYS_MS: readonly number[] = [5000, 60000];

/** Sync-status van één categorie, voor weergave in de "Mijn instellingen"-dialoog. */
export interface UserDataSyncCategoryStatus {
  /** Laatste localStorage-write op dit apparaat (ISO), incl. hydratatie vanaf de server. */
  localSavedOn: string | undefined;
  /** `ModifiedOn` van de laatst bevestigde server-write (ISO). */
  serverSavedOn: string | undefined;
  /** Er staat een wijziging klaar of onderweg naar de backend. */
  pending: boolean;
  /** De laatste server-call mislukte; lokaal werken gaat gewoon door. */
  offline: boolean;
}

interface SyncCategoryMeta {
  localSavedOn: string | null;
  serverSavedOn: string | null;
  /** Lokale wijziging die de server nog niet bevestigd heeft. */
  dirty: boolean;
}

interface UserDataSyncMetaV1 {
  schemaVersion: 1;
  settings: SyncCategoryMeta;
  appState: SyncCategoryMeta;
}

function createEmptyCategoryMeta(): SyncCategoryMeta {
  return { localSavedOn: null, serverSavedOn: null, dirty: false };
}

function createDefaultMeta(): UserDataSyncMetaV1 {
  return { schemaVersion: 1, settings: createEmptyCategoryMeta(), appState: createEmptyCategoryMeta() };
}

/**
 * Houdt de twee localStorage-blobs (app-instellingen `hdChartGlobalSettings`,
 * app-status `hdChartTabs`) per gebruiker in sync met de backend
 * (`api/userdata`). localStorage blijft de werk-laag: de app leest en schrijft
 * altijd lokaal (instant, offline-bestendig); deze service pusht wijzigingen
 * debounced (2 s) op de achtergrond en hydrateert bij het opstarten — vóór de
 * eerste render — vanaf de server.
 *
 * Reconciliatie bij init, per categorie:
 * - lokaal dirty en de server ongewijzigd sinds onze laatste sync → lokale blob
 *   pushen (offline-inhaalslag);
 * - anders, server heeft data → server-blob naar localStorage (server wint,
 *   ook bij een conflict met een ander apparaat: last-write-wins);
 * - GET mislukt → localStorage blijft leidend, status "offline", dirty blijft
 *   staan tot een push slaagt.
 *
 * Mislukte pushes kennen een begrensde retry-cyclus (5 s, dan 60 s, dan
 * opgeven — nooit oneindig op interval). Een nieuwe wijziging annuleert de
 * lopende cyclus volledig en start bij een fout zijn eigen cyclus; na een
 * geslaagde push kan er geen eerder gequeued commando meer vuren (één timer
 * per categorie, en een push verstuurt altijd de meest recente blob). Daarna
 * blijft herstel event-gedreven: volgende wijziging, browser-`online`-event
 * (netwerk terug), SignalR-hub-reconnect (backend terug), tab naar de
 * achtergrond, volgende app-start.
 *
 * Sync-metadata (per categorie: `localSavedOn`, `serverSavedOn`, `dirty`)
 * staat onder `userDataSyncMeta` en overleeft een refresh; `localStorage.clear()`
 * (logout, app-versie-bump) wist meta én blobs samen, waarna de eerstvolgende
 * init opnieuw vanaf de server hydrateert.
 */
@Injectable({ providedIn: 'root' })
export class UserDataSyncService {
  private readonly api = inject(UserDataApiService);
  private readonly userQueries = inject(UserStoreQueries);
  private readonly platformInstanceListener = inject(PlatformInstanceListener);
  private readonly platformMode = inject(PlatformModeService);
  private readonly writesPaused$ = toObservable(this.platformMode.writesPaused);

  private readonly _settingsStatus = signal<UserDataSyncCategoryStatus>({
    localSavedOn: undefined,
    serverSavedOn: undefined,
    pending: false,
    offline: false,
  });
  private readonly _appStateStatus = signal<UserDataSyncCategoryStatus>({
    localSavedOn: undefined,
    serverSavedOn: undefined,
    pending: false,
    offline: false,
  });

  readonly settingsStatus = this._settingsStatus.asReadonly();
  readonly appStateStatus = this._appStateStatus.asReadonly();

  private meta: UserDataSyncMetaV1 = createDefaultMeta();
  private readonly latestJson: Record<UserDataSyncCategory, string | undefined> = {
    settings: undefined,
    appState: undefined,
  };
  /**
   * Laatste blob waarvan zeker is dat de server hem heeft (geslaagde push of hydratatie). Een lokale
   * write die daar exact op terugvalt (bv. tab A → B → A binnen de wachttijd) hoeft niet gepusht.
   */
  private readonly lastSyncedJson: Record<UserDataSyncCategory, string | undefined> = {
    settings: undefined,
    appState: undefined,
  };
  private readonly debounceTimers: Record<UserDataSyncCategory, ReturnType<typeof setTimeout> | null> = {
    settings: null,
    appState: null,
  };
  private readonly retryTimers: Record<UserDataSyncCategory, ReturnType<typeof setTimeout> | null> = {
    settings: null,
    appState: null,
  };
  /** Aantal mislukte pogingen in de huidige cyclus (reset bij succes of nieuwe wijziging). */
  private readonly failedAttempts: Record<UserDataSyncCategory, number> = {
    settings: 0,
    appState: 0,
  };
  private readonly pushInFlight: Record<UserDataSyncCategory, boolean> = {
    settings: false,
    appState: false,
  };
  private initialized = false;
  private lateAuthenticationWatchActive = false;

  /**
   * Eénmalige init vanuit de APP_INITIALIZER-keten (na user-load, vóór de
   * eerste render). Zonder ingelogde gebruiker (bv. de token-refresh faalde
   * net tijdens een backend-release en de sessie herstelt pas later) wordt de
   * init uitgesteld tot de login alsnog binnenkomt — anders blijft de hele
   * synclaag (hydratatie vanaf het profiel + pushes) de rest van de sessie
   * stil en gaan wijzigingen bij de volgende localStorage-wipe verloren.
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    if (!this.userQueries.getIsAnyUserAuthenticated()) {
      this.watchForLateAuthentication();
      return;
    }
    this.initialized = true;

    this.meta = this.loadMeta();
    this.publishStatus('settings');
    this.publishStatus('appState');

    setUserDataLocalSyncListener((category, json) => this.onLocalSave(category, json));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.pushDirtyNow();
      }
    });
    // Netwerk hersteld (apparaat was offline) → niet-gepushte wijzigingen inhalen.
    window.addEventListener('online', () => this.pushDirtyNow());
    // Alleen-lezen voorbij (issue #51): wat intussen lokaal wijzigde, alsnog bewaren. Bewust bij
    // elke "niet gepauzeerd" en niet enkel na een gezien "gepauzeerd": het effect achter
    // toObservable kan die tussenstand overslaan. Zonder dirty categorie doet dit niets.
    this.writesPaused$.subscribe(paused => {
      if (!paused) {
        this.pushDirtyNow();
      }
    });
    // Platform weer bereikbaar terwijl het netwerk online bleef (backend was
    // down): de SignalR-hub reconnect automatisch; die overgang
    // onbeschikbaar→beschikbaar is ons "server is terug"-event. Bij een
    // normale boot verbindt de hub vóór deze subscribe en triggert er niets.
    let hubWasUnavailable = false;
    this.platformInstanceListener.isAvailable$.subscribe(available => {
      if (!available) {
        hubWasUnavailable = true;
        return;
      }
      if (hubWasUnavailable) {
        hubWasUnavailable = false;
        this.pushDirtyNow();
      }
    });

    const result = await this.api.getMine();
    let dto: PlatformUserDataDto | undefined;
    result.ifSuccess(value => (dto = value));
    if (dto === undefined) {
      this.markOffline('settings');
      this.markOffline('appState');
      return;
    }

    await this.reconcileCategory('settings', dto);
    await this.reconcileCategory('appState', dto);
  }

  /** Eénmalige wachter: initialiseert alsnog zodra de user alsnog geladen wordt. */
  private watchForLateAuthentication(): void {
    if (this.lateAuthenticationWatchActive) {
      return;
    }
    this.lateAuthenticationWatchActive = true;
    const subscription = this.userQueries.isAnyUserAuthenticated$.subscribe(authenticated => {
      if (!authenticated || this.initialized) {
        return;
      }
      subscription.unsubscribe();
      this.lateAuthenticationWatchActive = false;
      void this.initialize();
    });
  }

  private async reconcileCategory(category: UserDataSyncCategory, dto: PlatformUserDataDto): Promise<void> {
    const serverJson = this.serverJsonOf(category, dto) ?? undefined;
    const serverSavedOn = this.serverSavedOnOf(category, dto) ?? undefined;
    const localJson = localStorage.getItem(USER_DATA_CATEGORY_STORAGE_KEYS[category]) ?? undefined;
    const categoryMeta = this.meta[category];

    // Een ander apparaat schreef sinds onze laatste bevestigde sync: dan wint
    // de server, ook over lokale dirty wijzigingen (last-write-wins).
    const serverChangedSinceLastSync =
      serverSavedOn !== undefined && serverSavedOn !== (categoryMeta.serverSavedOn ?? undefined);

    // Lokaal pushen bij: (a) lege server met bestaande lokale blob (eerste keer
    // na deze feature / vers account: seeden), of (b) dirty wijzigingen terwijl
    // de server niet opschoof (offline-inhaalslag).
    const shouldPushLocal =
      localJson !== undefined &&
      (serverJson === undefined || (categoryMeta.dirty && !serverChangedSinceLastSync));

    if (shouldPushLocal) {
      this.latestJson[category] = localJson;
      await this.pushLatest(category);
      return;
    }

    if (serverJson !== undefined) {
      try {
        localStorage.setItem(USER_DATA_CATEGORY_STORAGE_KEYS[category], serverJson);
      } catch {
        console.warn(`userDataSync: hydrateren van '${category}' naar localStorage mislukt.`);
        return;
      }
      this.lastSyncedJson[category] = serverJson;
      this.meta[category] = {
        localSavedOn: moment().toISOString(),
        serverSavedOn: serverSavedOn ?? null,
        dirty: false,
      };
      this.persistMeta();
      this.publishStatus(category);
    }
    // Geen serverdata en lokaal niet dirty: niets te doen; de eerste lokale
    // wijziging creëert beide kanten.
  }

  /** Aangeroepen (synchroon) door de localStorage-funnels bij elke write. */
  private onLocalSave(category: UserDataSyncCategory, json: string): void {
    this.latestJson[category] = json;
    this.meta[category].localSavedOn = moment().toISOString();

    // Een nieuwe wijziging zet een eventuele lopende retry-cyclus volledig
    // stop; bij een fout start deze push zijn eigen cyclus.
    this.resetRetryCycle(category);

    const existingTimer = this.debounceTimers[category];
    if (existingTimer != null) {
      clearTimeout(existingTimer);
      this.debounceTimers[category] = null;
    }

    // Terug op wat de server al heeft: niets te pushen. Niet tijdens een lopende push: die vergelijkt
    // na afloop zelf met `latestJson` en pusht zo nodig opnieuw.
    if (json === this.lastSyncedJson[category] && !this.pushInFlight[category]) {
      this.meta[category].dirty = false;
      this.persistMeta();
      this.publishStatus(category, { pending: false });
      return;
    }

    this.meta[category].dirty = true;
    this.persistMeta();
    this.publishStatus(category, { pending: true });
    this.debounceTimers[category] = setTimeout(() => {
      this.debounceTimers[category] = null;
      void this.pushLatest(category);
    }, PUSH_DEBOUNCE_MS[category]);
  }

  private async pushLatest(category: UserDataSyncCategory): Promise<void> {
    // Alleen-lezen (issue #51): de api zou weigeren. De wijziging blijft lokaal en dirty, en gaat mee
    // zodra de modus voorbij is (zie initialize). Geen fout, geen retry-cyclus.
    if (this.platformMode.writesPaused()) {
      this.clearRetryTimer(category);
      // Dirty markeren: ook een seed-push (lege server, ongewijzigde lokale blob) is dan niet
      // dirty, en de inhaalslag (pushDirtyNow) zou hem anders overslaan.
      this.meta[category].dirty = true;
      this.persistMeta();
      this.publishStatus(category, { pending: true });
      return;
    }
    if (this.pushInFlight[category]) {
      // De lopende push detecteert na afloop dat `latestJson` opschoof en pusht opnieuw.
      return;
    }
    const json = this.latestJson[category];
    if (json === undefined) {
      return;
    }
    if (json === this.lastSyncedJson[category]) {
      // De server heeft deze blob al (bv. een wachtende timer na een doorgepushte wijziging).
      this.clearRetryTimer(category);
      this.meta[category].dirty = false;
      this.persistMeta();
      this.publishStatus(category, { pending: false });
      return;
    }
    // Eén cyclus tegelijk: een pending retry-timer is achterhaald zodra er
    // (door debounce, online-event of flush) toch een push start.
    this.clearRetryTimer(category);
    this.pushInFlight[category] = true;
    this.publishStatus(category, { pending: true });

    const result =
      category === 'settings' ? await this.api.updateAppSettings(json) : await this.api.updateAppState(json);

    this.pushInFlight[category] = false;

    let dto: PlatformUserDataDto | undefined;
    result.ifSuccess(value => (dto = value));
    if (dto === undefined) {
      this.markOffline(category);
      this.scheduleNextRetry(category);
      return;
    }

    this.resetRetryCycle(category);
    this.lastSyncedJson[category] = json;
    const serverSavedOn = this.serverSavedOnOf(category, dto) ?? null;
    const isStillLatest = this.latestJson[category] === json;
    this.meta[category].serverSavedOn = serverSavedOn;
    if (isStillLatest) {
      this.meta[category].dirty = false;
    }
    this.persistMeta();
    this.publishStatus(category, { pending: !isStillLatest, offline: false });

    if (!isStillLatest) {
      // Tijdens de call kwam er een nieuwe wijziging binnen: direct doorpushen.
      void this.pushLatest(category);
    }
  }

  /**
   * Pusht alles wat dirty is direct (zonder debounce af te wachten). Gebruikt
   * bij tab-verbergen en bij het `online`-event als event-gedreven inhaalslag;
   * telt als verse poging en reset dus de retry-cyclus.
   */
  private pushDirtyNow(): void {
    for (const category of ['settings', 'appState'] as const) {
      if (!this.meta[category].dirty) {
        continue;
      }
      this.resetRetryCycle(category);
      const timer = this.debounceTimers[category];
      if (timer != null) {
        clearTimeout(timer);
        this.debounceTimers[category] = null;
      }
      void this.pushLatest(category);
    }
  }

  /**
   * Plant de volgende poging van de begrensde cyclus (5 s → 60 s → opgeven).
   * Na opgeven blijft de categorie dirty; herstel volgt event-gedreven.
   */
  private scheduleNextRetry(category: UserDataSyncCategory): void {
    const attempt = this.failedAttempts[category];
    if (attempt >= RETRY_DELAYS_MS.length) {
      return;
    }
    this.failedAttempts[category] = attempt + 1;
    this.clearRetryTimer(category);
    this.retryTimers[category] = setTimeout(() => {
      this.retryTimers[category] = null;
      void this.pushLatest(category);
    }, RETRY_DELAYS_MS[attempt]);
  }

  private resetRetryCycle(category: UserDataSyncCategory): void {
    this.clearRetryTimer(category);
    this.failedAttempts[category] = 0;
  }

  private clearRetryTimer(category: UserDataSyncCategory): void {
    const timer = this.retryTimers[category];
    if (timer != null) {
      clearTimeout(timer);
      this.retryTimers[category] = null;
    }
  }

  private markOffline(category: UserDataSyncCategory): void {
    this.publishStatus(category, { pending: false, offline: true });
  }

  private publishStatus(category: UserDataSyncCategory, overrides?: Partial<UserDataSyncCategoryStatus>): void {
    const categoryMeta = this.meta[category];
    const status: UserDataSyncCategoryStatus = {
      localSavedOn: categoryMeta.localSavedOn ?? undefined,
      serverSavedOn: categoryMeta.serverSavedOn ?? undefined,
      pending: categoryMeta.dirty,
      offline: false,
      ...overrides,
    };
    if (category === 'settings') {
      this._settingsStatus.set(status);
    } else {
      this._appStateStatus.set(status);
    }
  }

  private serverJsonOf(category: UserDataSyncCategory, dto: PlatformUserDataDto): string | null | undefined {
    return category === 'settings' ? dto?.appSettingsJson : dto?.appStateJson;
  }

  private serverSavedOnOf(category: UserDataSyncCategory, dto: PlatformUserDataDto): string | null | undefined {
    return category === 'settings' ? dto?.appSettingsModifiedOn : dto?.appStateModifiedOn;
  }

  private loadMeta(): UserDataSyncMetaV1 {
    const raw = localStorage.getItem(USER_DATA_SYNC_META_STORAGE_KEY);
    if (!raw) {
      return createDefaultMeta();
    }
    try {
      const parsed = JSON.parse(raw) as UserDataSyncMetaV1;
      if (parsed?.schemaVersion !== 1) {
        return createDefaultMeta();
      }
      return {
        schemaVersion: 1,
        settings: { ...createEmptyCategoryMeta(), ...parsed.settings },
        appState: { ...createEmptyCategoryMeta(), ...parsed.appState },
      };
    } catch {
      console.warn('userDataSyncMeta: corrupte localStorage-inhoud genegeerd.');
      return createDefaultMeta();
    }
  }

  private persistMeta(): void {
    try {
      localStorage.setItem(USER_DATA_SYNC_META_STORAGE_KEY, JSON.stringify(this.meta));
    } catch {
      console.warn('userDataSyncMeta: wegschrijven naar localStorage mislukt.');
    }
  }
}
